import { AlarmDeviceConfiguration } from "@domain/project/configurations/alarm-device-configuration";
import { FlameDetectorConfiguration } from "@domain/project/configurations/flame-detector-configuration";
import { GasWarningCenterConfiguration } from "@domain/project/configurations/gas-warning-center-configuration";
import { DisplayConfiguration } from "@domain/project/configurations/monitoring/display-configuration";
import { KvmConfiguration } from "@domain/project/configurations/monitoring/kvm-configuration";
import { PatientMonitorConfiguration } from "@domain/project/configurations/monitoring/patient-monitor-configuration";
import { TelemetryConfiguration } from "@domain/project/configurations/monitoring/telemetry-configuration";
import { UtilityConfiguration } from "@domain/project/configurations/monitoring/utility-configuration";
import { PlasticSignConfiguration } from "@domain/project/configurations/plastic-sign-configuration";
import { ProductConfiguration } from "@domain/project/configurations/product-configuration";
import { SignalElementConfiguration } from "@domain/project/configurations/signal-element-configuration";
import { TransmitterConfiguration } from "@domain/project/configurations/transmitter-configuration";
import { ContactPerson } from "@domain/project/contact-person";
import { Customer } from "@domain/project/customer";
import { FloorplanItemReference } from "@domain/project/floorplan-item-reference";
import { Floorplan } from "@domain/project/floorplan/floorplan";
import { FloorplanPlaceholder } from "@domain/project/floorplan/floorplan-placeholder";
import {
  FloorplanPlaceholderPositionKind,
  FloorplanPlaceholderPositionPrefix,
} from "@domain/project/floorplan/floorplan-placeholder-position-kind";
import { ProductItemGroup } from "@domain/project/floorplan/product-item-group";
import { GoodsRecipient } from "@domain/project/goods-recipient";
import { ProjectEventType } from "@domain/project/project-event";
import { ProjectUpdatePublisher } from "@domain/project/project-update-publisher";
import { BusinessScope } from "@lib/constants/business-scope";
import { IsArrayOfInstancesOf, IsInstanceOf } from "@utils/class-validator/class-validator-constraints";
import { Exclude, Expose, instanceToPlain, plainToInstance, Transform, Type } from "class-transformer";
import { IsDate, IsNotEmpty, IsOptional, IsString, ValidateNested } from "class-validator";
import { v4 as uuidv4 } from "uuid";
import { CentralStationConfiguration } from "./configurations/monitoring/central-station-configuration";

export abstract class Project {
  @Exclude()
  private readonly placeholderPositionConfigs: Record<
    FloorplanPlaceholderPositionKind,
    {
      prefix: FloorplanPlaceholderPositionPrefix;
      getItems: (floorplan: Floorplan) => FloorplanPlaceholder[];
    }
  > = {
    [FloorplanPlaceholderPositionKind.ALARM_DEVICE]: {
      prefix: FloorplanPlaceholderPositionPrefix.ALARM_DEVICE,
      getItems: (floorplan: Floorplan) => floorplan.alarmDevicePlaceholders,
    },
    [FloorplanPlaceholderPositionKind.PLASTIC_SIGN]: {
      prefix: FloorplanPlaceholderPositionPrefix.PLASTIC_SIGN,
      getItems: (floorplan: Floorplan) => floorplan.plasticSignPlaceholders,
    },
    [FloorplanPlaceholderPositionKind.SIGNAL_ELEMENT]: {
      prefix: FloorplanPlaceholderPositionPrefix.SIGNAL_ELEMENT,
      getItems: (floorplan: Floorplan) => floorplan.signalElementPlaceholders,
    },
    [FloorplanPlaceholderPositionKind.PATIENT_MONITOR]: {
      prefix: FloorplanPlaceholderPositionPrefix.PATIENT_MONITOR,
      getItems: (floorplan: Floorplan) => floorplan.patientMonitorPlaceholders,
    },
    [FloorplanPlaceholderPositionKind.CENTRAL_STATION]: {
      prefix: FloorplanPlaceholderPositionPrefix.CENTRAL_STATION,
      getItems: (floorplan: Floorplan) => floorplan.centralStationPlaceholders,
    },
    [FloorplanPlaceholderPositionKind.TELEMETRY]: {
      prefix: FloorplanPlaceholderPositionPrefix.TELEMETRY,
      getItems: (floorplan: Floorplan) => floorplan.telemetryPlaceholders,
    },
    [FloorplanPlaceholderPositionKind.TRANSMITTER]: {
      prefix: FloorplanPlaceholderPositionPrefix.TRANSMITTER,
      getItems: (floorplan: Floorplan) => floorplan.transmitterPlaceholders,
    },
    [FloorplanPlaceholderPositionKind.FLAME_DETECTOR]: {
      prefix: FloorplanPlaceholderPositionPrefix.FLAME_DETECTOR,
      getItems: (floorplan: Floorplan) => floorplan.flameDetectorPlaceholders,
    },
    [FloorplanPlaceholderPositionKind.GAS_WARNING_CENTER]: {
      prefix: FloorplanPlaceholderPositionPrefix.GAS_WARNING_CENTER,
      getItems: (floorplan: Floorplan) => floorplan.gasWarningCenterPlaceholders,
    },
    [FloorplanPlaceholderPositionKind.KVM]: {
      prefix: FloorplanPlaceholderPositionPrefix.KVM,
      getItems: (floorplan: Floorplan) => floorplan.kvmPlaceholders,
    },
    [FloorplanPlaceholderPositionKind.DISPLAY]: {
      prefix: FloorplanPlaceholderPositionPrefix.DISPLAY,
      getItems: (floorplan: Floorplan) => floorplan.displayPlaceholders,
    },
    [FloorplanPlaceholderPositionKind.UTILITY]: {
      prefix: FloorplanPlaceholderPositionPrefix.UTILITY,
      getItems: (floorplan: Floorplan) => floorplan.utilityPlaceholders,
    },
  };

  @IsString()
  @Expose({ name: "id" })
  private _id: string;

  @IsInstanceOf(Customer)
  @ValidateNested()
  @Type(() => Customer)
  @Expose({ name: "customer" })
  private readonly _customer: Customer = new Customer();

  @IsInstanceOf(ContactPerson)
  @ValidateNested()
  @Type(() => ContactPerson)
  @Expose({ name: "contactPerson" })
  private readonly _contactPerson: ContactPerson = new ContactPerson();

  @IsInstanceOf(GoodsRecipient)
  @ValidateNested()
  @Type(() => GoodsRecipient)
  @Expose({ name: "goodsRecipient" })
  @Transform((params) => params.value || new GoodsRecipient(), {
    toClassOnly: true,
  })
  private readonly _goodsRecipient: GoodsRecipient = new GoodsRecipient();

  @Exclude()
  private _cachedFloorplans: Floorplan[] = [];

  @IsDate()
  @Type(() => Date)
  @Expose({ name: "lastModified" })
  private _lastModified: Date = new Date();

  @IsDate()
  @IsOptional()
  @Type(() => Date)
  public lastCloudSync?: Date;

  @IsString()
  @IsOptional()
  public cloudId?: string;

  @IsOptional()
  @Exclude()
  private _updatePublisher?: ProjectUpdatePublisher;

  @IsString()
  @IsOptional()
  @Expose({ name: "status" })
  @Transform(({ value }) => value)
  public status?: string;

  @IsString()
  public name: string;

  @IsString()
  @IsNotEmpty()
  public country: string;

  @IsString()
  @IsOptional()
  public version?: string;

  @IsString()
  @IsOptional()
  public placeName?: string;

  @IsString()
  @IsOptional()
  public opportunityId?: string;

  @IsString()
  @IsOptional()
  public opportunityLink?: string;

  @IsString()
  @IsOptional()
  public notes?: string;

  @IsString()
  @IsOptional()
  public crmEntryId?: string;

  @ValidateNested()
  @IsArrayOfInstancesOf(ProductItemGroup)
  @Type(() => ProductItemGroup)
  @Expose({ name: "groups" })
  @Transform(({ value }) => value ?? [], { toClassOnly: true })
  private _groups: ProductItemGroup[] = [];

  @ValidateNested()
  @IsArrayOfInstancesOf(FloorplanItemReference)
  @Type(() => FloorplanItemReference)
  @Expose({ name: "productItemReferences" })
  @Transform(({ value }) => value ?? [], { toClassOnly: true })
  private _productItemReferences: FloorplanItemReference[] = [];

  constructor(
    id: string,
    name: string,
    status: string,
    country: string,
    version?: string,
    placeName?: string,
    notes?: string,
    crmEntryId?: string,
  ) {
    this._id = id;
    this.name = name;
    this.status = status;
    this.country = country;
    this.version = version;
    this.placeName = placeName;
    this.notes = notes;
    this.crmEntryId = crmEntryId;
  }

  createFromProject(project: Project) {
    const newProject = this.createCopyFromProject(project);
    newProject._id = uuidv4();
    return newProject;
  }

  @Exclude()
  set updatePublisher(updatePublisher: ProjectUpdatePublisher | undefined) {
    this._updatePublisher = updatePublisher;
  }

  set cachedFloorplans(floorplans: Floorplan[]) {
    this._cachedFloorplans = floorplans;
  }

  get cachedFloorplans(): Floorplan[] {
    return this._cachedFloorplans;
  }

  get id(): string {
    return this._id;
  }

  get customer(): Customer {
    return this._customer;
  }

  get contactPerson(): ContactPerson {
    return this._contactPerson;
  }

  get goodsRecipient(): GoodsRecipient {
    return this._goodsRecipient;
  }

  get lastModified(): Date {
    return this._lastModified;
  }

  set lastModified(date: Date) {
    this._lastModified = date;
  }

  get groups() {
    return this._groups;
  }

  get productItemReferences(): FloorplanItemReference[] {
    return this._productItemReferences;
  }

  addProductItemReference(reference: FloorplanItemReference): void {
    const alreadyExists = this._productItemReferences.some(
      (r) => r.floorplanId === reference.floorplanId && r.floorplanProductItemId === reference.floorplanProductItemId,
    );
    if (!alreadyExists) {
      this._productItemReferences.push(reference);
    }
  }

  removeProductItemReference(floorplanId: string, floorplanProductItemId: string): void {
    this._productItemReferences = this._productItemReferences.filter(
      (r) => !(r.floorplanId === floorplanId && r.floorplanProductItemId === floorplanProductItemId),
    );
  }

  getProductItemReference(floorplanId: string, floorplanProductItemId: string): FloorplanItemReference | undefined {
    return this._productItemReferences.find(
      (r) => r.floorplanId === floorplanId && r.floorplanProductItemId === floorplanProductItemId,
    );
  }

  getGroupsOnFloorplan(floorplanId: string): ProductItemGroup[] {
    return this._groups.filter((group) => group.references.some((r) => r.floorplanId === floorplanId));
  }

  addGroup(group: ProductItemGroup) {
    this._groups.push(group);
  }

  removeGroup(groupId: string) {
    this._groups = this._groups.filter((group) => group.id !== groupId);
  }

  removeGroupIfEmpty(group: ProductItemGroup) {
    if (group.references.length === 0) {
      this.removeGroup(group.id);
    }
  }

  /**
   * Remaps all references to floorplan ids (in product item groups and product item references) to the
   * new floorplan ids after floorplans have been copied to a new project. This ensures groups and product
   * item references are correctly carried over when a project is copied.
   */
  remapFloorplanReferences(floorplanIdMap: ReadonlyMap<string, string>): void {
    if (floorplanIdMap.size === 0) {
      return;
    }
    this._groups.forEach((group) => {
      group.references.forEach((reference) => {
        const newFloorplanId = floorplanIdMap.get(reference.floorplanId);
        if (newFloorplanId) {
          reference.floorplanId = newFloorplanId;
        }
      });
    });
    this._productItemReferences.forEach((reference) => {
      const newFloorplanId = floorplanIdMap.get(reference.floorplanId);
      if (newFloorplanId) {
        reference.floorplanId = newFloorplanId;
      }
    });
  }

  abstract get businessScope(): BusinessScope;

  get inSyncWithCloud(): boolean {
    return !!this.lastCloudSync && this.lastCloudSync >= this.lastModified;
  }

  abstract validate(): void;

  updateCloudSync(cloudId: string) {
    this.lastCloudSync = new Date();
    this.cloudId = cloudId;
    this._updatePublisher?.publishProjectEvent(ProjectEventType.SYNCED_TO_CLOUD, this);
  }

  countPlacedAlarmDevices(alarmDevice: AlarmDeviceConfiguration): number {
    let amount = 0;
    this._cachedFloorplans.forEach((floorplan) => (amount += floorplan.countPlacedAlarmDevices(alarmDevice)));
    return amount;
  }

  countPlacedTransmitters(transmitter: TransmitterConfiguration): number {
    let amount = 0;
    this._cachedFloorplans.forEach((floorplan) => (amount += floorplan.countPlacedTransmitters(transmitter)));
    return amount;
  }

  countPlacedFlameDetectors(flameDetector: FlameDetectorConfiguration): number {
    let amount = 0;
    this._cachedFloorplans.forEach((floorplan) => (amount += floorplan.countPlacedFlameDetectors(flameDetector)));
    return amount;
  }

  countPlacedGasWarningCenters(gasWarningCenter: GasWarningCenterConfiguration): number {
    let amount = 0;
    this._cachedFloorplans.forEach((floorplan) => (amount += floorplan.countPlacedGasWarningCenters(gasWarningCenter)));
    return amount;
  }

  countPlacedSignalElements(signalElement: SignalElementConfiguration): number {
    let amount = 0;
    this._cachedFloorplans.forEach((floorplan) => (amount += floorplan.countPlacedSignalElements(signalElement)));
    return amount;
  }

  countPlacedPlasticSigns(plasticSign: PlasticSignConfiguration): number {
    let amount = 0;
    this._cachedFloorplans.forEach((floorplan) => (amount += floorplan.countPlacedPlasticSigns(plasticSign)));
    return amount;
  }

  countPlacedPatientMonitors(patientMonitor: PatientMonitorConfiguration): number {
    let amount = 0;
    this._cachedFloorplans.forEach((floorplan) => (amount += floorplan.countPlacedPatientMonitors(patientMonitor)));
    return amount;
  }

  countPlacedCentralStations(centralStation: CentralStationConfiguration): number {
    let amount = 0;
    this._cachedFloorplans.forEach((floorplan) => (amount += floorplan.countPlacedCentralStations(centralStation)));
    return amount;
  }

  countPlacedTelemetries(telemetry: TelemetryConfiguration): number {
    let amount = 0;
    this._cachedFloorplans.forEach((floorplan) => (amount += floorplan.countPlacedTelemetries(telemetry)));
    return amount;
  }

  countPlacedUtilities(utility: UtilityConfiguration): number {
    let amount = 0;
    this._cachedFloorplans.forEach((floorplan) => (amount += floorplan.countPlacedUtilities(utility)));
    return amount;
  }

  countPlacedKvms(kvm: KvmConfiguration): number {
    let amount = 0;
    this._cachedFloorplans.forEach((floorplan) => (amount += floorplan.countPlacedKvms(kvm)));
    return amount;
  }

  countPlacedDisplays(display: DisplayConfiguration): number {
    let amount = 0;
    this._cachedFloorplans.forEach((floorplan) => (amount += floorplan.countPlacedDisplays(display)));
    return amount;
  }

  setAdditionalProductListQuantity(config: ProductConfiguration, quantity: number) {
    const nextQuantity = Number.isFinite(quantity) && quantity > 0 ? Math.floor(quantity) : 0;
    if (config.additionalProductListQuantity === nextQuantity) {
      return;
    }

    config.additionalProductListQuantity = nextQuantity;
    this.lastModified = new Date();
    this.setPositionNumbers();
    this.publishUpdate(ProjectEventType.PRODUCT_CONFIGURATION_PRODUCT_LIST_QUANTITY_UPDATED, config);
  }

  getFloorplanById(floorplanId: string) {
    return this._cachedFloorplans.find((floorplan) => floorplan.id === floorplanId);
  }

  copy(): Project {
    const copy = this.createCopyFromProject(this);
    copy._id = uuidv4();
    copy.name = `${this.name} Copy`;
    copy._lastModified = new Date();
    copy.lastCloudSync = undefined;
    copy.cloudId = undefined;
    return copy;
  }

  abstract setPositionNumbers(): void;

  setGasWarningCenterPlaceholderPositionIds(positionNumber?: number) {
    this.setPlaceholderPositionIdsByKind(FloorplanPlaceholderPositionKind.GAS_WARNING_CENTER, positionNumber);
  }

  setTransmitterPlaceholderPositionIds(positionNumber?: number) {
    this.setPlaceholderPositionIdsByKind(FloorplanPlaceholderPositionKind.TRANSMITTER, positionNumber);
  }

  setFlameDetectorPlaceholderPositionIds(positionNumber?: number) {
    this.setPlaceholderPositionIdsByKind(FloorplanPlaceholderPositionKind.FLAME_DETECTOR, positionNumber);
  }

  setSignalElementPlaceholderPositionIds(positionNumber?: number) {
    this.setPlaceholderPositionIdsByKind(FloorplanPlaceholderPositionKind.SIGNAL_ELEMENT, positionNumber);
  }

  setPlasticSignPlaceholderPositionIds(positionNumber?: number) {
    this.setPlaceholderPositionIdsByKind(FloorplanPlaceholderPositionKind.PLASTIC_SIGN, positionNumber);
  }

  setAlarmDevicePlaceholderPositionIds(positionNumber?: number) {
    this.setPlaceholderPositionIdsByKind(FloorplanPlaceholderPositionKind.ALARM_DEVICE, positionNumber);
  }

  setPatientMonitorPlaceholderPositionIds(positionNumber?: number) {
    this.setPlaceholderPositionIdsByKind(FloorplanPlaceholderPositionKind.PATIENT_MONITOR, positionNumber);
  }

  setCentralStationPlaceholderPositionIds(positionNumber?: number) {
    this.setPlaceholderPositionIdsByKind(FloorplanPlaceholderPositionKind.CENTRAL_STATION, positionNumber);
  }

  setTelemetryPlaceholderPositionIds(positionNumber?: number) {
    this.setPlaceholderPositionIdsByKind(FloorplanPlaceholderPositionKind.TELEMETRY, positionNumber);
  }

  setKvmPlaceholderPositionIds(positionNumber?: number) {
    this.setPlaceholderPositionIdsByKind(FloorplanPlaceholderPositionKind.KVM, positionNumber);
  }

  setDisplayPlaceholderPositionIds(positionNumber?: number) {
    this.setPlaceholderPositionIdsByKind(FloorplanPlaceholderPositionKind.DISPLAY, positionNumber);
  }

  setUtilityPlaceholderPositionIds(positionNumber?: number) {
    this.setPlaceholderPositionIdsByKind(FloorplanPlaceholderPositionKind.UTILITY, positionNumber);
  }

  refreshPlaceholderPositionIds(placeholder: FloorplanPlaceholder, positionNumber?: number) {
    const kind = placeholder.getPlaceholderPositionKind();
    if (!kind) {
      this.setPositionNumbers();
      return;
    }

    if (positionNumber === undefined) {
      if (!placeholder.usesPlaceholderPrefixPositionId()) {
        this.setPositionNumbers();
        return;
      }
      this.setPrefixedPlaceholderPositionIdsByKind(kind);
      return;
    }

    this.setPlaceholderPositionIdsByKind(kind, positionNumber);
  }

  getPlaceholderPositionIdPrefix(
    kind: FloorplanPlaceholderPositionKind | undefined,
  ): FloorplanPlaceholderPositionPrefix | undefined {
    if (!kind) {
      return undefined;
    }

    return this.placeholderPositionConfigs[kind].prefix;
  }

  private setPlaceholderPositionIdsByKind(kind: FloorplanPlaceholderPositionKind, positionNumber?: number) {
    const config = this.placeholderPositionConfigs[kind];
    this.setPlaceholderPositionIds(config.getItems, config.prefix, positionNumber);
  }

  private setPrefixedPlaceholderPositionIdsByKind(kind: FloorplanPlaceholderPositionKind) {
    const config = this.placeholderPositionConfigs[kind];
    this.setPrefixedPlaceholderPositionIds(config.getItems, config.prefix);
  }

  setExZonePositionIds() {
    let indexCount0 = 0;
    let indexCount1 = 0;
    let indexCount2 = 0;
    this._cachedFloorplans.map((floorplan) => {
      floorplan.exZones.map((exZone) => {
        switch (exZone.exZoneType) {
          case "ZONE_0":
            exZone.positionId = "EX0." + (indexCount0 += 1);
            break;
          case "ZONE_1":
            exZone.positionId = "EX1." + (indexCount1 += 1);
            break;
          case "ZONE_2":
            exZone.positionId = "EX2." + (indexCount2 += 1);
        }
      });
    });
  }

  setDustExZonePositionIds() {
    let indexCount20 = 0;
    let indexCount21 = 0;
    let indexCount22 = 0;
    this._cachedFloorplans.map((floorplan) => {
      floorplan.dustExZones.map((dustExZone) => {
        switch (dustExZone.dustExZoneType) {
          case "ZONE_20":
            dustExZone.positionId = "D-EX20." + (indexCount20 += 1);
            break;
          case "ZONE_21":
            dustExZone.positionId = "D-EX21." + (indexCount21 += 1);
            break;
          case "ZONE_22":
            dustExZone.positionId = "D-EX22." + (indexCount22 += 1);
        }
      });
    });
  }

  setWildcardZonePositionIds() {
    let indexCount0 = 0;
    let indexCount1 = 0;
    let indexCount2 = 0;
    this._cachedFloorplans.map((floorplan) => {
      floorplan.wildcardZones.map((zone) => {
        switch (zone.type) {
          case "ZONE_0":
            zone.positionId = "Z0." + (indexCount0 += 1);
            break;
          case "ZONE_1":
            zone.positionId = "Z1." + (indexCount1 += 1);
            break;
          case "ZONE_2":
            zone.positionId = "Z2." + (indexCount2 += 1);
        }
      });
    });
  }

  setDangerAreaPositionIds() {
    let indexCount = 0;
    this._cachedFloorplans.map((floorplan) => {
      floorplan.dangerAreas.map((dangerArea) => (dangerArea.positionId = "GB." + (indexCount += 1)));
    });
  }

  setImagePositionIds() {
    let indexCount = 0;
    this._cachedFloorplans.map((floorplan) => {
      floorplan.images.map((image) => {
        image.positionId = "F." + (indexCount += 1);
      });
    });
  }

  setPipelinePositionIds() {
    let indexCount = 0;
    this._cachedFloorplans.map((floorplan) => {
      floorplan.pipelines.map((pipeline) => (pipeline.positionId = "r." + (indexCount += 1)));
    });
  }

  setCableRoutePositionIds() {
    let indexCount = 0;
    this._cachedFloorplans.map((floorplan) => {
      floorplan.cableRoutes.map((cableRoute) => (cableRoute.positionId = "k." + (indexCount += 1)));
    });
  }

  setProtectiveTubePositionIds() {
    let indexCount = 0;
    this._cachedFloorplans.map((floorplan) => {
      floorplan.protectiveTubes.map((protectiveTube) => (protectiveTube.positionId = "s." + (indexCount += 1)));
    });
  }

  setWirePositionIds() {
    let indexCount = 0;
    this._cachedFloorplans.map((floorplan) => {
      floorplan.wires.map((wire) => (wire.positionId = "l." + (indexCount += 1)));
    });
  }

  setAirPathPositionIds() {
    let indexCountSupplyAir = 0;
    let indexCountExhaustAir = 0;
    this._cachedFloorplans.forEach((floorplan) =>
      floorplan.airPaths.forEach(
        (airPath) =>
          (airPath.positionId =
            airPath.direction === "SUPPLY_AIR" ? "zu." + (indexCountSupplyAir += 1) : "ab." + (indexCountExhaustAir += 1)),
      ),
    );
  }

  setFloorplanTextPositionIds() {
    let indexCount = 0;
    this._cachedFloorplans.map((floorplan) => {
      floorplan.floorplanTexts.map((floorplanText) => (floorplanText.positionId = (indexCount += 1).toString()));
    });
  }

  setFloorplanFreeTextPositionIds() {
    let indexCount = 0;
    this._cachedFloorplans.map((floorplan) => {
      floorplan.floorplanFreeTexts.map((floorplanFreeText) => (floorplanFreeText.positionId = "ft." + (indexCount += 1)));
    });
  }

  setMeasurementLinePositionIds() {
    let indexCount = 0;
    this._cachedFloorplans.map((floorplan) => {
      floorplan.measurementLines.map((measurementLine) => (measurementLine.positionId = "b." + (indexCount += 1)));
    });
  }

  setReferenceMeasurePositionIds() {
    let indexCount = 0;
    this._cachedFloorplans.map((floorplan) => {
      if (floorplan.referenceMeasure) floorplan.referenceMeasure.positionId = "r." + (indexCount += 1);
    });
  }

  setIsolationRoomPositionIds() {
    let indexCount = 0;
    this._cachedFloorplans.map((floorplan) => {
      floorplan.isolationRooms.map((isolationRoom) => (isolationRoom.positionId = "IR." + (indexCount += 1)));
    });
  }

  setItRoomPositionIds() {
    let indexCount = 0;
    this._cachedFloorplans.map((floorplan) => {
      floorplan.itRooms.map((itRoom) => (itRoom.positionId = "IT." + (indexCount += 1)));
    });
  }

  setOperatingTheatrePositionIds() {
    let indexCount = 0;
    this._cachedFloorplans.map((floorplan) => {
      floorplan.operatingTheatres.map((operatingTheatre) => (operatingTheatre.positionId = "OT." + (indexCount += 1)));
    });
  }

  setProcedureRoomPositionIds() {
    let indexCount = 0;
    this._cachedFloorplans.map((floorplan) => {
      floorplan.procedureRooms.map((procedureRoom) => (procedureRoom.positionId = "PR." + (indexCount += 1)));
    });
  }

  setStandardRoomPositionIds() {
    let indexCount = 0;
    this._cachedFloorplans.map((floorplan) => {
      floorplan.standardRooms.map((standardRoom) => (standardRoom.positionId = "SR." + (indexCount += 1)));
    });
  }

  setAllPositionIds() {
    this.setPositionNumbers();
    this.setExZonePositionIds();
    this.setDustExZonePositionIds();
    this.setWildcardZonePositionIds();
    this.setDangerAreaPositionIds();
    this.setImagePositionIds();
    this.setPipelinePositionIds();
    this.setCableRoutePositionIds();
    this.setProtectiveTubePositionIds();
    this.setWirePositionIds();
    this.setAirPathPositionIds();
    this.setFloorplanTextPositionIds();
    this.setFloorplanFreeTextPositionIds();
    this.setMeasurementLinePositionIds();
    this.setIsolationRoomPositionIds();
    this.setItRoomPositionIds();
    this.setOperatingTheatrePositionIds();
    this.setProcedureRoomPositionIds();
    this.setStandardRoomPositionIds();
  }

  publishUpdate(type: ProjectEventType, subject: any) {
    this.lastModified = new Date();
    if (this._updatePublisher) {
      this._updatePublisher.publishProjectEvent(type, subject);
    }
  }

  protected deleteConfiguration<T extends ProductConfiguration>(
    configurationToDelete: T,
    existingConfigurations: T[],
    deleteEventType: ProjectEventType,
    deleteFromFloorplanFn: (floorplan: Floorplan, config: T) => void,
  ) {
    const indexToDelete = existingConfigurations.indexOf(configurationToDelete);
    if (indexToDelete < 0) {
      return;
    }

    this._cachedFloorplans.forEach((floorplan) => {
      deleteFromFloorplanFn(floorplan, configurationToDelete);
    });

    existingConfigurations.splice(indexToDelete, 1);
    this.publishUpdate(deleteEventType, configurationToDelete);
  }

  protected setPositionNumber<T extends ProductConfiguration>(
    positionNumber: number,
    configs: T[],
    updatePositionIdsFn: (floorplan: Floorplan, config: T) => void,
  ) {
    configs.forEach((config) => {
      if (config.countPlacedProducts() > 0 || config.additionalProductListQuantity > 0) {
        config.positionNumber = positionNumber.toString();
        this._cachedFloorplans.forEach((floorplan) => {
          updatePositionIdsFn(floorplan, config);
        });
        positionNumber += 1;
      } else {
        config.positionNumber = undefined;
      }
    });
  }

  public countPlacedConfigurations<T extends ProductConfiguration>(configs: T[]) {
    return configs.filter((config) => config.countPlacedProducts() > 0 || config.additionalProductListQuantity > 0).length;
  }

  protected setAdditionalEntryPositionNumbers<T extends { positionNumber?: string; quantity: number }>(
    positionNumber: number,
    entries: T[],
  ) {
    entries.forEach((entry) => {
      if (entry.quantity > 0) {
        entry.positionNumber = positionNumber.toString();
        positionNumber += 1;
      } else {
        entry.positionNumber = undefined;
      }
    });
  }

  protected countPlaceholdersWithProductId<T extends FloorplanPlaceholder>(
    floorplanPlaceholdersCallback: (floorplan: Floorplan) => T[],
  ): number {
    return this._cachedFloorplans
      .flatMap((floorplan: Floorplan) => floorplanPlaceholdersCallback(floorplan))
      .filter(
        (placeholder: T) =>
          placeholder.products.length > 0 && placeholder.products.filter((placeholderProduct) => placeholderProduct.id).length,
      ).length;
  }

  private setPlaceholderPositionIds<T extends FloorplanPlaceholder>(
    floorplanPlaceholdersCallback: (floorplan: Floorplan) => T[],
    positionIdPrefix: FloorplanPlaceholderPositionPrefix,
    positionNumber?: number,
  ) {
    let indexCount = 0;
    this._cachedFloorplans.forEach((floorplan) => {
      floorplanPlaceholdersCallback(floorplan)
        .filter((placeholder: T) => !placeholder.products.filter((placeholderProduct) => placeholderProduct.id).length)
        .map((placeholder, index) => ({ placeholder, index }))
        .sort((a, b) => {
          const orderDifference =
            this.getPrefixedPlaceholderSortOrder(a.placeholder.positionId, positionIdPrefix) -
            this.getPrefixedPlaceholderSortOrder(b.placeholder.positionId, positionIdPrefix);
          return orderDifference !== 0 ? orderDifference : a.index - b.index;
        })
        .map(({ placeholder }) => placeholder)
        .forEach((placeholder) => {
          placeholder.positionId = positionIdPrefix + (indexCount += 1);
        });
      if (positionNumber === undefined) {
        return;
      }
      floorplanPlaceholdersCallback(floorplan)
        .filter((placeholder: T) => placeholder.products.filter((placeholderProduct) => placeholderProduct.id).length)
        .map((placeholder, index) => ({ placeholder, index }))
        .sort((a, b) => {
          const orderDifference =
            this.getConfiguredPlaceholderSortOrder(a.placeholder.positionId, positionIdPrefix) -
            this.getConfiguredPlaceholderSortOrder(b.placeholder.positionId, positionIdPrefix);
          return orderDifference !== 0 ? orderDifference : a.index - b.index;
        })
        .map(({ placeholder }) => placeholder)
        .forEach((placeholder) => {
          placeholder.positionId = positionNumber!.toString() + ".1";
          positionNumber!++;
        });
    });
  }

  private setPrefixedPlaceholderPositionIds<T extends FloorplanPlaceholder>(
    floorplanPlaceholdersCallback: (floorplan: Floorplan) => T[],
    positionIdPrefix: FloorplanPlaceholderPositionPrefix,
  ) {
    let indexCount = 0;
    this._cachedFloorplans.forEach((floorplan) => {
      floorplanPlaceholdersCallback(floorplan)
        .map((placeholder, index) => ({ placeholder, index }))
        .sort((a, b) => {
          const orderDifference =
            this.getPrefixedPlaceholderSortOrder(a.placeholder.positionId, positionIdPrefix) -
            this.getPrefixedPlaceholderSortOrder(b.placeholder.positionId, positionIdPrefix);
          return orderDifference !== 0 ? orderDifference : a.index - b.index;
        })
        .map(({ placeholder }) => placeholder)
        .forEach((placeholder) => {
          placeholder.positionId = positionIdPrefix + (indexCount += 1);
        });
    });
  }

  private getPrefixedPlaceholderSortOrder(positionId: string | undefined, positionIdPrefix: FloorplanPlaceholderPositionPrefix) {
    const prefixedIndex = this.getPrefixedPlaceholderPositionIndex(positionId, positionIdPrefix);
    if (prefixedIndex !== undefined) {
      return prefixedIndex;
    }

    const configuredPositionNumber = this.getConfiguredPlaceholderPositionNumber(positionId);
    if (configuredPositionNumber !== undefined) {
      // offset into a second sort tier, after all prefixed items
      return Number.MAX_SAFE_INTEGER / 3 + configuredPositionNumber;
    }

    return Number.MAX_SAFE_INTEGER;
  }

  private getConfiguredPlaceholderSortOrder(
    positionId: string | undefined,
    positionIdPrefix: FloorplanPlaceholderPositionPrefix,
  ) {
    const configuredPositionNumber = this.getConfiguredPlaceholderPositionNumber(positionId);
    if (configuredPositionNumber !== undefined) {
      return configuredPositionNumber;
    }

    const prefixedIndex = this.getPrefixedPlaceholderPositionIndex(positionId, positionIdPrefix);
    if (prefixedIndex !== undefined) {
      // offset into a second sort tier, after all configured items
      return Number.MAX_SAFE_INTEGER / 3 + prefixedIndex;
    }

    return Number.MAX_SAFE_INTEGER;
  }

  private getPrefixedPlaceholderPositionIndex(
    positionId: string | undefined,
    positionIdPrefix: FloorplanPlaceholderPositionPrefix,
  ) {
    if (!positionId?.startsWith(positionIdPrefix)) {
      return undefined;
    }

    const index = Number(positionId.slice(positionIdPrefix.length));
    return Number.isFinite(index) ? index : undefined;
  }

  private getConfiguredPlaceholderPositionNumber(positionId?: string) {
    const positionNumber = Number(positionId?.split(".")[0]);
    return Number.isFinite(positionNumber) ? positionNumber : undefined;
  }

  private createCopyFromProject(project: Project) {
    if (project) {
      const constructor = Object.getPrototypeOf(project)?.constructor as (new (...args: unknown[]) => Project) | undefined;
      if (!constructor) {
        throw new Error("Cannot determine constructor for project copy");
      }
      return plainToInstance(constructor, instanceToPlain(project)) as Project;
    }
    throw new Error("No project provided");
  }
}
